import * as React from 'react';
import { useState, useRef, useCallback } from 'react';
import { Card, Typography, emptyFragment } from '@execonline-inc/execonline-ui';
import { Maybe } from 'maybeasy';
import { Link as RouterLink } from 'react-router-dom';
import { DashboardSummaryPayload } from '../../DashboardSummaryStore/Types';
import { Link } from '../../Resource/Types';
import MarkdownRenderer from './MarkdownRenderer';
import AIRationale from './AIRationale';
import DateRangePopover from './DateRangePopover';
import SparklesIcon from '../Cutesies/SparklesIcon';
import FilterIcon from '../Cutesies/FilterIcon';
import RefreshIcon from '../Cutesies/RefreshIcon';
import CopyRoundedIcon from '../Cutesies/CopyRoundedIcon';
import Checkmark from '../Cutesies/Checkmark';
import ChevronRightIcon from '../Cutesies/ChevronRightIcon';
import {
  formatDateRange,
  formatGeneratedTime,
  getDefaultDateRange,
  copySummaryToClipboard,
} from './utils';
import { T } from '../../Translations';
import { useClickOutside } from './useClickOutside';

// Constants
const COPY_SUCCESS_DISPLAY_MS = 2000;

interface Props {
  summary: DashboardSummaryPayload;
  onRefresh?: () => void;
  fullReportLink: Maybe<Link>;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
}

const SuccessState: React.FC<Props> = ({
  summary,
  onRefresh,
  fullReportLink,
  onDateRangeChange,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  const { summary: summaryText, headline, rationale, metadata, filters } = summary;
  const defaults = getDefaultDateRange();
  const startDate = filters.startDate.getOrElse(() => defaults.startDate);
  const endDate = filters.endDate.getOrElse(() => defaults.endDate);
  const dateRange = formatDateRange(startDate, endDate);
  const generatedTime = formatGeneratedTime(metadata.generatedAt);

  const closeDatePicker = useCallback(() => setShowDatePicker(false), []);

  // Close date picker when clicking outside
  useClickOutside([datePickerRef, settingsButtonRef], showDatePicker, closeDatePicker);

  const handleCopy = async () => {
    const success = await copySummaryToClipboard(headline, summaryText, rationale);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), COPY_SUCCESS_DISPLAY_MS);
    }
  };

  const handleDateRangeSubmit = useCallback(
    (newStartDate: string, newEndDate: string) => {
      setShowDatePicker(false);
      if (onDateRangeChange) {
        onDateRangeChange(newStartDate, newEndDate);
      }
    },
    [onDateRangeChange],
  );

  return (
    <Card className="w-full pt-6 pr-6 pb-6 pl-6" radius="lg" data-intercom-target="ai-summary-card">
      {/* Header */}
      <div className="flex items-start justify-between pb-4 mb-4 border-b border-gray-200">
        <div>
          <div className="flex items-center space-x-2">
            <SparklesIcon className="w-5 h-5" color="#3b8860" />
            <Typography variant="bodyMedium" fontWeight="semibold" color="dark">
              <T kind="Action Plan Impact Summary" />
            </Typography>
          </div>
          <Typography variant="caption" className="text-gray-500">
            {dateRange && `${dateRange} • `}
            {generatedTime}
          </Typography>
        </div>

        {/* Action buttons */}
        <div className="flex items-center space-x-1">
          {/* Settings/Date picker button */}
          <div className="relative">
            <button
              ref={settingsButtonRef}
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`p-2 rounded-lg transition-colors ${
                showDatePicker
                  ? 'bg-gray-100/30'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/30'
              }`}
              title="Adjust date range"
              data-intercom-target="ai-summary-date-filter-button"
            >
              <FilterIcon className="w-5 h-5" />
            </button>

            {/* Date picker popover */}
            {showDatePicker && (
              <div ref={datePickerRef} className="absolute right-0 top-full mt-2 z-50">
                <DateRangePopover
                  initialStartDate={startDate}
                  initialEndDate={endDate}
                  onSubmit={handleDateRangeSubmit}
                  onClose={closeDatePicker}
                />
              </div>
            )}
          </div>

          {/* Refresh button */}
          <button
            onClick={onRefresh}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100/30 rounded-lg transition-colors"
            title="Regenerate summary"
            data-intercom-target="ai-summary-refresh-button"
          >
            <RefreshIcon className="w-5 h-5" />
          </button>

          {/* Copy button */}
          <button
            onClick={handleCopy}
            className={`p-2 rounded-lg transition-colors ${
              copySuccess
                ? 'text-green-600 bg-green-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100/30'
            }`}
            title={copySuccess ? 'Copied!' : 'Copy to clipboard'}
            data-intercom-target="ai-summary-copy-button"
          >
            {copySuccess ? (
              <Checkmark className="w-5 h-5 fill-current" />
            ) : (
              <CopyRoundedIcon className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Headline */}
      {headline
        .map((h) => (
          <p key="headline" className="text-green-600 mb-4 text-base">
            {h}
          </p>
        ))
        .getOrElse(emptyFragment)}

      {/* Summary content */}
      <MarkdownRenderer content={summaryText} expandable={false} />

      {/* AI Rationale section */}
      <div className="mt-4">
        <AIRationale items={rationale.getOrElse(() => [])} />
      </div>

      {/* View Full Report link - only shown when link exists */}
      {fullReportLink
        .map((link) => (
          <div key="view-full-report" className="mt-4 flex justify-end">
            <RouterLink
              to={link.href}
              className="flex items-center text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
              data-intercom-target="ai-summary-view-full-report-link"
            >
              <T kind="View Full Report" />
              <ChevronRightIcon className="w-4 h-4 ml-1" />
            </RouterLink>
          </div>
        ))
        .getOrElse(emptyFragment)}
    </Card>
  );
};

export default SuccessState;
